import { HEADERS, isApiErrorBody, type ApiErrorBody, type ErrorCode } from '@cqinv/shared';

/**
 * Cliente HTTP base.
 *
 * Las credenciales van en una cookie `HttpOnly` que el navegador no puede leer
 * (patrón BFF, ADR-0010), así que aquí no hay ningún token: sólo
 * `credentials: 'include'`.
 */
export class ApiError extends Error {
  constructor(
    readonly code: ErrorCode,
    message: string,
    readonly status: number,
    readonly requestId: string,
    readonly details: ApiErrorBody['error']['details'] | undefined = undefined,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/** Error de red o servidor caído: no hay código de dominio que mostrar. */
export const ERROR_SIN_RED: ErrorCode = 'SERVICE_UNAVAILABLE';

export async function apiFetch<T>(ruta: string, init: RequestInit = {}): Promise<T> {
  let respuesta: Response;
  try {
    respuesta = await fetch(ruta, {
      ...init,
      credentials: 'include',
      headers: {
        Accept: 'application/json',
        ...(typeof init.body === 'string' ? { 'Content-Type': 'application/json' } : {}),
        ...init.headers,
      },
    });
  } catch {
    throw new ApiError(
      ERROR_SIN_RED,
      'No hay conexión con el servidor. Comprueba la red e inténtalo de nuevo.',
      0,
      '',
    );
  }

  const requestId = respuesta.headers.get(HEADERS.requestId) ?? '';

  if (respuesta.status === 204) return undefined as T;

  const cuerpo: unknown = await respuesta.json().catch(() => null);

  if (!respuesta.ok) {
    if (isApiErrorBody(cuerpo)) {
      if (cuerpo.error.code === 'SESSION_EXPIRED' && typeof window !== 'undefined') {
        const silencio = ruta.includes('/session/me') || ruta.includes('/health');
        if (!silencio) {
          const destino = `${window.location.pathname}${window.location.search}`;
          window.location.assign(
            `/api/auth/login?returnTo=${encodeURIComponent(destino.startsWith('/') ? destino : '/')}`,
          );
        }
      }
      throw new ApiError(
        cuerpo.error.code,
        cuerpo.error.message,
        respuesta.status,
        cuerpo.error.requestId,
        cuerpo.error.details,
      );
    }
    throw new ApiError(
      'INTERNAL_ERROR',
      'Ocurrió un error inesperado. Vuelve a intentarlo.',
      respuesta.status,
      requestId,
    );
  }

  return cuerpo as T;
}
